/**
 * Static Rapier colliders: the M1 asteroid field, the Harrow derelict and (M2) stations and rock fragments
 * (spec §8, plan §2.5, M2 plan §2.4). Each set is one fixed body placed at its float64 anchor rebased to the
 * physics origin (D-011, D-013), so the existing `recenter` moves it with every other body; colliders hang off it
 * at float32 local offsets. Built on rare events (a preset change, a station model arriving, a fracture), never
 * per tick; removing one collider of a set (a depleted rock) is equally rare.
 */
import RAPIER from '@dimforge/rapier3d-compat'
import type { Collider, RigidBody } from '@dimforge/rapier3d-compat'
import type { DerelictPose, FieldLayout } from '../world/field'
import { EID_ASTEROID, EID_DERELICT, STATIC_COLLISION_GROUPS, type Physics } from './physics'

/**
 * One fixed body and the handles of its colliders (all mapped in `Physics.colliderEid`). A handle is `NO_HANDLE`
 * where a collider was never built (a removed field rock) or was removed since (`removeStaticCollider`), so
 * indices stay aligned with whatever the set was built from (the field layout's rock indices).
 */
export interface StaticColliders {
  readonly body: RigidBody
  readonly handles: number[]
}

/** Triangle mesh in the station's local frame (`DerelictBuild.collider`, a station's `COL_` trimesh). */
export interface StaticTrimesh {
  readonly vertices: Float32Array
  readonly indices: Uint32Array
}

/** Float64 world pose of a static set (the derelict's, a station's). */
export type StaticPose = DerelictPose

/** A missing collider in `StaticColliders.handles`. */
export const NO_HANDLE = -1

/**
 * One static ball per rock at `radius × colliderScale`, mapped to EID_ASTEROID. The body sits at the
 * layout origin (the envelope centre), so ball offsets stay small enough for float32 (≈ 1 mm at 10 km).
 * Rocks whose stable id (`layout.id`) is in `removed` get no collider (`NO_HANDLE`).
 */
export function addFieldColliders(p: Physics, layout: FieldLayout, colliderScale: number, removed?: ReadonlySet<number>): StaticColliders {
  const o = layout.origin
  const body = p.world.createRigidBody(
    RAPIER.RigidBodyDesc.fixed().setTranslation(o.x - p.origin.x, o.y - p.origin.y, o.z - p.origin.z),
  )
  const handles: number[] = new Array<number>(layout.count)
  for (let i = 0; i < layout.count; i++) {
    if (removed?.has(layout.id[i])) {
      handles[i] = NO_HANDLE
      continue
    }
    const desc = RAPIER.ColliderDesc.ball(layout.radius[i] * colliderScale)
      .setTranslation(layout.x[i] - o.x, layout.y[i] - o.y, layout.z[i] - o.z)
      .setCollisionGroups(STATIC_COLLISION_GROUPS)
    const handle = p.world.createCollider(desc, body).handle
    p.colliderEid.set(handle, EID_ASTEROID)
    handles[i] = handle
  }
  return { body, handles }
}

/**
 * A static trimesh (local vertices) as one fixed body at the float64 `pose`, mapped to `eid` (EID_DERELICT,
 * EID_STATION, ...). Default trimesh flags: assembled kits are not closed manifolds, which Rapier's internal-edge
 * fixing requires. `label` names the mesh in errors.
 */
export function addStaticTrimesh(p: Physics, trimesh: StaticTrimesh, pose: StaticPose, eid: number, label = 'static'): StaticColliders {
  if (trimesh.indices.length < 3 || trimesh.indices.length % 3 !== 0 || trimesh.vertices.length < 9) {
    throw new Error(
      `addStaticTrimesh: ${label} trimesh needs whole triangles (got ${trimesh.vertices.length / 3} vertices, ${trimesh.indices.length} indices)`,
    )
  }
  const { position: t, rotation: q } = pose
  const body = p.world.createRigidBody(
    RAPIER.RigidBodyDesc.fixed()
      .setTranslation(t.x - p.origin.x, t.y - p.origin.y, t.z - p.origin.z)
      .setRotation({ x: q.x, y: q.y, z: q.z, w: q.w }),
  )
  let handle: number
  try {
    const desc = RAPIER.ColliderDesc.trimesh(trimesh.vertices, trimesh.indices).setCollisionGroups(STATIC_COLLISION_GROUPS)
    handle = p.world.createCollider(desc, body).handle
  } catch (err) {
    p.world.removeRigidBody(body)
    throw new Error(`addStaticTrimesh: ${label} trimesh could not be built (${String(err)})`, { cause: err })
  }
  p.colliderEid.set(handle, eid)
  return { body, handles: [handle] }
}

/** The derelict's static trimesh (station-local vertices) at the float64 `pose`, mapped to EID_DERELICT. */
export function addDerelictCollider(p: Physics, trimesh: StaticTrimesh, pose: DerelictPose): StaticColliders {
  return addStaticTrimesh(p, trimesh, pose, EID_DERELICT, 'derelict')
}

/**
 * Static balls on one fixed body anchored at the float64 point `origin` (pick it near the balls so the float32
 * offsets stay exact), mapped to `eid`. `xyz` holds float64 world centres, interleaved (3 per ball); `radii` one
 * radius per ball. Used for rock fragments.
 */
export function addStaticBalls(
  p: Physics,
  originX: number,
  originY: number,
  originZ: number,
  xyz: ArrayLike<number>,
  radii: ArrayLike<number>,
  eid: number,
): StaticColliders {
  if (xyz.length !== radii.length * 3) throw new Error(`addStaticBalls: ${xyz.length} coordinates for ${radii.length} radii`)
  const body = p.world.createRigidBody(
    RAPIER.RigidBodyDesc.fixed().setTranslation(originX - p.origin.x, originY - p.origin.y, originZ - p.origin.z),
  )
  const handles: number[] = new Array<number>(radii.length)
  for (let i = 0; i < radii.length; i++) {
    const desc = RAPIER.ColliderDesc.ball(radii[i])
      .setTranslation(xyz[i * 3] - originX, xyz[i * 3 + 1] - originY, xyz[i * 3 + 2] - originZ)
      .setCollisionGroups(STATIC_COLLISION_GROUPS)
    const handle = p.world.createCollider(desc, body).handle
    p.colliderEid.set(handle, eid)
    handles[i] = handle
  }
  return { body, handles }
}

/**
 * Removes one collider of `set` (and its eid mapping) and marks its slot `NO_HANDLE`, so the other slots keep
 * their indices. Returns false when `handle` is not a live collider of the set. Like a new collider, the removal
 * reaches raycasts after the next Rapier step.
 */
export function removeStaticCollider(p: Physics, set: StaticColliders, handle: number): boolean {
  if (handle === NO_HANDLE) return false
  const k = set.handles.indexOf(handle)
  if (k < 0) return false
  const collider: Collider | null | undefined = p.world.getCollider(handle)
  if (collider) p.world.removeCollider(collider, false)
  p.colliderEid.delete(handle)
  set.handles[k] = NO_HANDLE
  return true
}

/** Removes a static set: its eid mappings, colliders and body. */
export function removeStaticColliders(p: Physics, set: StaticColliders): void {
  for (let i = 0; i < set.handles.length; i++) if (set.handles[i] !== NO_HANDLE) p.colliderEid.delete(set.handles[i])
  p.world.removeRigidBody(set.body)
}

/** Removes the field's colliders (a quality change rebuilds them: plan §2.5). */
export const removeFieldColliders: (p: Physics, set: StaticColliders) => void = removeStaticColliders
